// Kurzbefehl-Token verwalten (Projektplan 10.4, Weg b). Flaeche, die es im
// Original nicht gibt, deshalb frei gestaltet.
//
// "Fertiger Kurzbefehl" aus dem Plan heisst hier: eine Kurzanleitung zum
// Selbstbauen in der Shortcuts-App, kein importierbares .shortcut-Objekt.
// Das Erzeugen eines solchen Objekts braucht eine iCloud-Freigabe aus der
// Shortcuts-App selbst - das kann von diesem Server aus nicht erzeugt
// werden, siehe docs/decisions.md.

import { useState, type FormEvent } from "react";
import { Check, Copy, Trash2 } from "lucide-react";
import { fehlertext } from "../lib/api.js";
import { useQuickAddTokenAnlegen, useQuickAddTokenWiderrufen, useQuickAddTokens } from "../lib/quickAdd.js";
import type { Sammlung } from "../lib/me.js";
import { Hinweis, Karte, Knopf } from "./Bausteine.js";

function datum(iso: string | null): string {
  if (!iso) return "noch nie benutzt";
  return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function KopierZeile({ wert }: { wert: string }) {
  const [kopiert, setKopiert] = useState(false);
  async function kopieren() {
    await navigator.clipboard.writeText(wert);
    setKopiert(true);
    setTimeout(() => setKopiert(false), 2000);
  }
  return (
    <div className="flex items-center gap-2 rounded border border-neutral-200 bg-flaeche-inhalt p-2">
      <input readOnly value={wert} className="min-w-0 flex-1 bg-transparent text-xs" />
      <button type="button" onClick={() => void kopieren()} aria-label="Kopieren" className="min-h-11 min-w-11 shrink-0">
        {kopiert ? <Check size={16} /> : <Copy size={16} />}
      </button>
    </div>
  );
}

export function KurzbefehlEinstellungen({ sammlungen }: { sammlungen: Sammlung[] }) {
  const [label, setLabel] = useState("");
  const [ziele, setZiele] = useState<Set<number>>(new Set());
  const [neu, setNeu] = useState<{ token: string; url: string } | null>(null);

  const tokens = useQuickAddTokens();
  const anlegen = useQuickAddTokenAnlegen();
  const widerrufen = useQuickAddTokenWiderrufen();

  function zielUmschalten(id: number) {
    setZiele((vorher) => {
      const naechste = new Set(vorher);
      if (naechste.has(id)) naechste.delete(id);
      else naechste.add(id);
      return naechste;
    });
  }

  async function anlegenAbsenden(e: FormEvent) {
    e.preventDefault();
    if (label.trim().length < 2) return;
    const antwort = await anlegen.mutateAsync({ label: label.trim(), defaultTargets: [...ziele] });
    setNeu({ token: antwort.token, url: antwort.url });
    setLabel("");
    setZiele(new Set());
  }

  return (
    <Karte
      kinder={
        <>
          <h2 className="mb-1 text-base font-medium">Kurzbefehl (iOS)</h2>
          <p className="mb-3 text-sm text-neutral-600">
            Ein Kennzeichen eintragen, ohne die App zu öffnen - über die Shortcuts-App und ihre
            Widget-Galerie.
          </p>

          {tokens.data && tokens.data.length > 0 && (
            <ul className="mb-3 space-y-2">
              {tokens.data.map((t) => (
                <li key={t.id} className="flex items-center justify-between gap-2 border-t border-neutral-100 pt-2 text-sm first:border-t-0 first:pt-0">
                  <div>
                    <div className="font-medium">{t.label}</div>
                    <div className="text-xs text-neutral-500">{datum(t.lastUsedAt)}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => void widerrufen.mutateAsync(t.id)}
                    aria-label={`${t.label} widerrufen`}
                    className="flex min-h-11 min-w-11 items-center justify-center text-red-700"
                  >
                    <Trash2 size={18} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {anlegen.error && <Hinweis art="fehler" kinder={fehlertext(anlegen.error)} />}

          {neu ? (
            <div className="space-y-3 rounded bg-neutral-50 p-3 text-sm">
              <Hinweis art="gut" kinder="Token erstellt. Er wird nur dieses eine Mal angezeigt." />
              <div>
                <p className="mb-1 font-medium">Token</p>
                <KopierZeile wert={neu.token} />
              </div>
              <div>
                <p className="mb-1 font-medium">Adresse</p>
                <KopierZeile wert={neu.url} />
              </div>
              <div>
                <p className="mb-1 font-medium">So baust du den Kurzbefehl</p>
                <ol className="list-decimal space-y-1 pl-5 text-neutral-700">
                  <li>In der Kurzbefehle-App einen neuen Kurzbefehl anlegen.</li>
                  <li>Aktion „Text" hinzufügen, „Frage bei Ausführung" aktivieren, Frage z. B. „Kennzeichen?".</li>
                  <li>
                    Aktion „Inhalte von URL abrufen" hinzufügen: Methode <strong>POST</strong>, Adresse wie oben,
                    Kopfzeile <strong>Authorization</strong> = <strong>Bearer {"<Token wie oben>"}</strong>,
                    Anfragetext JSON mit dem Feld <strong>code</strong> = der Text aus Schritt 2.
                  </li>
                  <li>Aktion „Wörterbuchwert abrufen" mit dem Schlüssel „text", danach „Ergebnis anzeigen".</li>
                  <li>
                    Kurzbefehl auf dem Home-Bildschirm lange drücken, „Widgets bearbeiten" wählen und den
                    Kurzbefehl zur Widget-Galerie hinzufügen.
                  </li>
                </ol>
              </div>
              <Knopf art="neben" onClick={() => setNeu(null)} kinder="Fertig" />
            </div>
          ) : (
            <form className="space-y-2 border-t border-neutral-100 pt-3" onSubmit={(e) => void anlegenAbsenden(e)}>
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Name, z. B. Auto"
                maxLength={60}
                className="min-h-11 w-full rounded border border-neutral-300 px-3 text-sm"
              />
              <div className="space-y-1">
                <p className="text-xs text-neutral-500">
                  Ziel (nichts ausgewählt = deine persönliche Sammlung):
                </p>
                {sammlungen.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm text-neutral-700">
                    <input type="checkbox" checked={ziele.has(s.id)} onChange={() => zielUmschalten(s.id)} className="h-4 w-4" />
                    {s.name}
                  </label>
                ))}
              </div>
              <Knopf type="submit" art="neben" disabled={anlegen.isPending || label.trim().length < 2} kinder="Kurzbefehl-Token erstellen" />
            </form>
          )}
        </>
      }
    />
  );
}
